import { Clickable, Col, Icon, Row, Text } from 'components';
import { useOutsideClick } from 'core';
import Picker from 'emoji-picker-react';
import { theme } from 'lib';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { NationCodeDropdown } from './';

const Container = styled.div`
  width: 100%;
  height: 43px;
  box-sizing: border-box;
  padding: ${(props) => props.innerPad || (props.phone ? '0 6px' : '0 16px')};

  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  border-radius: 50px;
  background-color: ${(props) => props.bg || 'transparent'};
  font-size: 16px;
  border-width: ${(props) => (props.error || props.isFocused ? '2px' : '1px')};
  border-style: solid;
  border-color: ${(props) =>
    props.error ? theme.SECONDARY : props.isFocused ? '#000' : theme.GREY_50};

  ${(props) =>
    props.multiline &&
    `
    border-radius: 16px;
    padding: 0;

    ${
      props.numberOfLines &&
      `
      max-height: 100%;
      height: 100%;
    `
    }

  `};

  ${(props) =>
    props.ht &&
    `
    height: ${props.ht};
    align-items: flex-start;
  `}

  ${(props) =>
    props.horizontalCenter &&
    `
    align-items: center;
  `}
`;

const StyledInput = styled.input`
  font-family: CeraPro;
  flex: 1;
  font-size: 16px;
  ${(props) => props.center && `text-align: center;`};
  ${(props) => props.maxLength && `max-length: ${props.maxLength};`}
`;

const StyledTextArea = styled.textarea`
  font-family: CeraPro;
  flex: 1;
  font-size: 16px;
  ${(props) => props.center && `text-align: center;`};
  outline: none;
  border: none;
  resize: none;
  ${(props) => props.maxLength && `max-length: ${props.maxLength};`}

  ${(props) =>
    !props.emoji
      ? `
      padding: 10px;
      margin: 5px;
    `
      : `
      padding: 10px 10px 10px 0;
      margin: 5px 5px 5px 0;
    `}
  height: ${(props) => `calc(${props.ht || '100%'} - 37px)`}
`;

const ShoutInput = (props) => {
  const { value, limit = 0 } = props;
  const [hide, setHide] = useState(props.password),
    [showEmojis, setShowEmojis] = useState(false),
    [inherentFocused, setInherentFocused] = useState(false),
    emojiRef = useRef(null),
    defaultInputRef = useRef(null),
    inputRef = props.inputRef || defaultInputRef;
  const passedLimit = (props.value?.length || 0) - limit > limit;

  useOutsideClick(emojiRef, () => {
    setShowEmojis(false);
    setInherentFocused(false);
  }, [inputRef.current?.className, 'emoji-button']);

  useEffect(() => {
    inputRef.current?.addEventListener('keypress', onEnter);

    return () => {
      inputRef.current?.removeEventListener('keypress', onEnter);
    };
  }, []);

  function handleBlur(e) {
    props.setIsFocused(false);
    if (!props.emoji) return setInherentFocused(false);

    let parentElement = e.nativeEvent.explicitOriginalTarget;
    if (!parentElement) return;

    // Get parent element until you get to a div so you don't deal with svgs or
    // paths which are hard to differantiate
    while (parentElement.localName !== 'div') {
      parentElement = parentElement.parentElement;
    }

    // If the pressed/released element isn't the emoji_button or
    // within the emoji selector
    if (
      (props.emoji &&
        (parentElement.className?.search('emoji') !== -1 ||
          parentElement.className?.search('content-wrapper-epr') !== -1)) ||
      showEmojis
    )
      return;

    setInherentFocused(false);
  }

  function handleFocused() {
    props.setIsFocused(true);
    setInherentFocused(true);
  }

  function onEnter(e) {
    if (e.key !== 'Enter') return;

    e.preventDefault();
    props.onSend && props.onSend(e.target.value);
  }

  function handleEmoji(emoji) {
    const ref = inputRef?.current;

    if (!ref) return console.log('ref is required');

    /*
      Read the live DOM value/selection instead of props.value — the emoji
      picker memoizes its config on mount, so this callback can be a stale
      closure holding text the user has since edited or cleared. The DOM
      node always reflects the current controlled value.
    */
    const liveValue = ref.value || '';
    const start = ref.selectionStart ?? liveValue.length;
    const end = ref.selectionEnd ?? start;

    props.onChange({
      target: {
        value: liveValue.substring(0, start) + emoji + liveValue.substring(end)
      }
    });
  }

  return (
    <React.Fragment>
      {props.title && (
        <Text b2 med marg={props.rounded ? '0 0 8px' : '0 0 4px 0px'}>
          {props.title}
        </Text>
      )}

      <Container {...props} isFocused={inherentFocused}>
        <Row center between wid="100%">
          {props.phone && (
            <NationCodeDropdown
              selected={props.nation}
              handleNationSelect={props.handleNationSelect}
            />
          )}

          {props.emoji && (
            <Col
              ref={emojiRef}
              noFlex
              marg={props.emojiMarg || '15px 4px 15px 8px'}
              pos="relative"
              className="emoji-button"
            >
              <Clickable onClick={() => setShowEmojis(!showEmojis)}>
                <Icon type="emoji" col={theme.GREY_60} className="emoji-button" />
              </Clickable>

              {showEmojis && (
                <Row pos="absolute" zIndex="2" left="0" bottom="40px">
                  <Picker onEmojiClick={({ emoji }) => handleEmoji(emoji)} />
                </Row>
              )}
            </Col>
          )}

          {props.multiline ? (
            <StyledTextArea
              {...props}
              placeholderTextColor={theme.GREY_60}
              placeholderStyle={{ fontSize: 16 }}
              type={hide ? 'password' : 'text'}
              onFocus={() => handleFocused()}
              onBlur={(e) => handleBlur(e)}
              ref={inputRef}
            />
          ) : (
            <StyledInput
              {...props}
              placeholderTextColor={theme.GREY_60}
              placeholderStyle={{ fontSize: 16 }}
              type={hide ? 'password' : 'text'}
              onFocus={() => handleFocused()}
              onBlur={(e) => handleBlur(e)}
              ref={inputRef}
            />
          )}

          {props.hasRemove && !!props.value?.length && (
            <Clickable onClick={() => props.onChange({ target: { value: '' } })}>
              <Icon type="close" dimensions={14} col={theme.GREY_50} />
            </Clickable>
          )}

          {props.onSend && !!props.value?.length && (
            <Clickable onClick={() => props.onSend(props.value)}>
              <Icon type="send" dimensions={18} col={theme.TALE} />
            </Clickable>
          )}

          {props.password && (
            <Clickable onClick={() => setHide(!hide)}>
              <Icon type={hide ? 'visibleOff' : 'visibleOn'} dimensions={22} col={theme.GREY_60} />
            </Clickable>
          )}
        </Row>
      </Container>

      {(props.error || props.limit) && (
        <Row between pad="4px 16px 0">
          <Text c1 bold col={theme.SECONDARY}>
            {props.error || ''}
          </Text>

          {props.limit && (
            <Text c1 bold col={passedLimit ? theme.SECONDARY : props.col}>
              {props.value?.length} / {props.limit}
            </Text>
          )}
        </Row>
      )}
    </React.Fragment>
  );
};

export default ShoutInput;
